import { School, Category, Product, NewsItem, SiteSettings, ImpactStats, CustomerMessage, DaySchedule } from '../types';

export const DEFAULT_OPENING_SCHEDULE: DaySchedule[] = [
  { day: 'Monday', isOpen: false, openTime: '09:00', closeTime: '15:00' },
  { day: 'Tuesday', isOpen: false, openTime: '09:00', closeTime: '15:00' },
  { day: 'Wednesday', isOpen: true, openTime: '09:00', closeTime: '15:00' },
  { day: 'Thursday', isOpen: true, openTime: '09:00', closeTime: '15:00' },
  { day: 'Friday', isOpen: true, openTime: '09:00', closeTime: '15:00' },
  { day: 'Saturday', isOpen: false, openTime: '09:00', closeTime: '15:00' },
  { day: 'Sunday', isOpen: false, openTime: '09:00', closeTime: '15:00' }
];

export const formatOpeningHours = (schedule?: DaySchedule[], fallbackStr?: string): string => {
  if (!schedule || schedule.length === 0) {
    return fallbackStr || 'Wednesdays 09:00 - 15:00 | Thursdays 09:00 - 15:00 | Fridays 09:00 - 15:00';
  }
  const openDays = schedule.filter((s) => s.isOpen);
  if (openDays.length === 0) return 'Closed';
  
  const dayPlurals: Record<string, string> = {
    Monday: 'Mondays',
    Tuesday: 'Tuesdays',
    Wednesday: 'Wednesdays',
    Thursday: 'Thursdays',
    Friday: 'Fridays',
    Saturday: 'Saturdays',
    Sunday: 'Sundays'
  };

  return openDays
    .map((s) => `${dayPlurals[s.day] || s.day} ${s.openTime} - ${s.closeTime}`)
    .join(' | ');
};

export const INITIAL_SCHOOLS: School[] = [
  {
    id: 'canon-peter-hall',
    name: 'Canon Peter Hall Primary School',
    website: 'https://www.canonpeterhall.co.uk',
    type: 'Primary School',
    address: 'Pelham Road, Immingham, DN40 1JW',
    town: 'Immingham',
    county: 'North East Lincolnshire',
    active: true,
    displayOrder: 1,
    colour: '#0284C7',
    logoUrl: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 'coomb-briggs',
    name: 'Coomb Briggs Primary School',
    website: 'https://www.coombbriggsprimary.co.uk',
    type: 'Primary School',
    address: 'Roval Drive, Immingham, DN40 2DY',
    town: 'Immingham',
    county: 'North East Lincolnshire',
    active: true,
    displayOrder: 2,
    colour: '#DC2626',
    logoUrl: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 'eastfield',
    name: 'Eastfield Primary Academy',
    website: 'https://www.eastfieldprimaryacademy.co.uk',
    type: 'Primary Academy',
    address: 'Margaret Street, Immingham, DN40 1LD',
    town: 'Immingham',
    county: 'North East Lincolnshire',
    active: true,
    displayOrder: 3,
    colour: '#16A34A',
    logoUrl: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 'oasis-immingham',
    name: 'Oasis Academy Immingham',
    website: 'https://www.oasisacademyimmingham.org',
    type: 'Secondary Academy',
    address: 'Pelham Road, Immingham, DN40 1JU',
    town: 'Immingham',
    county: 'North East Lincolnshire',
    active: true,
    displayOrder: 4,
    colour: '#123D8D',
    logoUrl: 'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 'pilgrim',
    name: 'Pilgrim Academy',
    website: 'https://www.pilgrimacademy.co.uk',
    type: 'Primary Academy',
    address: 'Allestree Drive, Immingham, DN40 1QA',
    town: 'Immingham',
    county: 'North East Lincolnshire',
    active: true,
    displayOrder: 5,
    colour: '#7C3AED',
    logoUrl: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=200&q=80'
  }
];

export const INITIAL_CATEGORIES: Category[] = [
  { id: 'blazers', name: 'Blazers', icon: 'Shirt', description: 'Tailored school blazers with embroidered crests' },
  { id: 'jumpers', name: 'Jumpers', icon: 'Vest', description: 'V-neck & crewneck knitted sweaters' },
  { id: 'cardigans', name: 'Cardigans', icon: 'Shirt', description: 'Buttoned knitted cardigans' },
  { id: 'shirts', name: 'Shirts', icon: 'Shirt', description: 'Long & short sleeve white/blue school shirts' },
  { id: 'polo-shirts', name: 'Polo Shirts', icon: 'Shirt', description: 'Embroidered school polo shirts' },
  { id: 'trousers', name: 'Trousers', icon: 'Scissors', description: 'Navy & grey school trousers' },
  { id: 'shorts', name: 'Shorts', icon: 'Scissors', description: 'Tailored school shorts & summer shorts' },
  { id: 'skirts', name: 'Skirts', icon: 'Scissors', description: 'Pleated & A-line skirts & pinafores' },
  { id: 'pe-shirts', name: 'PE Shirts', icon: 'Activity', description: 'Sports t-shirts and polo shirts' },
  { id: 'pe-shorts', name: 'PE Shorts', icon: 'Activity', description: 'Athletic shorts & skorts' },
  { id: 'tracksuits', name: 'Tracksuits', icon: 'Layers', description: 'PE hoodies, track jackets & joggers' },
  { id: 'coats', name: 'Coats', icon: 'Shield', description: 'Waterproof coats & winter jackets' },
  { id: 'shoes', name: 'Shoes', icon: 'Footprints', description: 'Black school shoes & plimsolls' },
  { id: 'bags', name: 'Bags', icon: 'ShoppingBag', description: 'Book bags & rucksacks' },
  { id: 'ties', name: 'Ties', icon: 'Sparkles', description: 'Clip-on & traditional school ties' },
  { id: 'accessories', name: 'Accessories', icon: 'Package', description: 'Hats, scarves, hair ties & aprons' }
];

export const INITIAL_PRODUCTS: Product[] = [
  {
    id: 'prod-1',
    schoolId: 'oasis-immingham',
    categoryId: 'blazers',
    name: 'Oasis Academy Immingham Fitted Blazer',
    color: 'Navy Blue',
    gender: 'Unisex',
    size: 'Chest 32"',
    description: 'Official Oasis Academy Immingham navy blazer with silver braided trim and embroidered academy crest. Dry cleaned and ready to wear.',
    sizes: [
      { id: 'sz-1a', size: 'Chest 28"', stock: 1 },
      { id: 'sz-1b', size: 'Chest 30"', stock: 2 },
      { id: 'sz-1c', size: 'Chest 32"', stock: 4 },
      { id: 'sz-1d', size: 'Chest 34"', stock: 0 },
      { id: 'sz-1e', size: 'Chest 36"', stock: 1 }
    ],
    collectionNotes: 'Collect from Bert Boyden Community Centre drop desk.',
    featured: true,
    recentlyAdded: true,
    isPopular: true,
    images: [
      'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-01'
  },
  {
    id: 'prod-2',
    schoolId: 'oasis-immingham',
    categoryId: 'pe-shirts',
    name: 'Oasis Academy Sports PE Polo Shirt',
    color: 'Royal Blue / Navy',
    gender: 'Unisex',
    size: 'Medium (36")',
    description: 'Breathable sports mesh fabric with moisture wicking. Features official school logo. Unused with original tags attached.',
    sizes: [
      { id: 'sz-2a', size: 'Chest 28"', stock: 3 },
      { id: 'sz-2b', size: 'Chest 32"', stock: 5 },
      { id: 'sz-2c', size: 'Chest 36"', stock: 2 },
      { id: 'sz-2d', size: 'Chest 40"', stock: 1 }
    ],
    collectionNotes: 'Available immediately.',
    featured: true,
    recentlyAdded: true,
    isNewDonation: true,
    images: [
      'https://images.unsplash.com/photo-1521572267360-ee0c2909d518?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-05'
  },
  {
    id: 'prod-3',
    schoolId: 'coomb-briggs',
    categoryId: 'jumpers',
    name: 'Coomb Briggs Knitted Crewneck Jumper',
    color: 'Royal Blue',
    gender: 'Unisex',
    size: 'Age 7-8 (28")',
    description: 'Soft cotton-rich knit jumper with bright Coomb Briggs logo on left chest. Warm, comfortable and wash-tested.',
    sizes: [
      { id: 'sz-3a', size: '26"', stock: 3 },
      { id: 'sz-3b', size: '28"', stock: 5 },
      { id: 'sz-3c', size: '30"', stock: 2 },
      { id: 'sz-3d', size: '32"', stock: 0 },
      { id: 'sz-3e', size: '34"', stock: 4 }
    ],
    collectionNotes: 'Washed and folded.',
    featured: true,
    recentlyAdded: false,
    isPopular: true,
    images: [
      'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-07-28'
  },
  {
    id: 'prod-4',
    schoolId: 'eastfield',
    categoryId: 'cardigans',
    name: 'Eastfield Primary Knitted Cardigan',
    color: 'Navy Blue',
    gender: 'Girls',
    size: 'Age 5-6 (26")',
    description: 'Button-front navy cardigan with Eastfield Academy embroidered crest. Durable fabric, clean and well cared for.',
    sizes: [
      { id: 'sz-4a', size: '24"', stock: 2 },
      { id: 'sz-4b', size: '26"', stock: 1 },
      { id: 'sz-4c', size: '28"', stock: 3 },
      { id: 'sz-4d', size: '30"', stock: 1 }
    ],
    collectionNotes: 'Multiple sizes available.',
    featured: false,
    recentlyAdded: true,
    images: [
      'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-04'
  },
  {
    id: 'prod-5',
    schoolId: 'pilgrim',
    categoryId: 'trousers',
    name: 'Smart Grey Pleated School Trousers',
    color: 'Charcoal Grey',
    gender: 'Boys',
    size: 'Age 9-10 (Waist 24")',
    description: 'Adjustable waist pleated school trousers with Teflon stain-resistant finish. Unworn with tags.',
    sizes: [
      { id: 'sz-5a', size: 'Age 5-6 (Waist 20")', stock: 2 },
      { id: 'sz-5b', size: 'Age 7-8 (Waist 22")', stock: 4 },
      { id: 'sz-5c', size: 'Age 9-10 (Waist 24")', stock: 3 },
      { id: 'sz-5d', size: 'Age 11-12 (Waist 26")', stock: 2 }
    ],
    collectionNotes: 'Great quality donation from local family.',
    featured: true,
    recentlyAdded: true,
    isNewDonation: true,
    images: [
      'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-06'
  },
  {
    id: 'prod-6',
    schoolId: 'canon-peter-hall',
    categoryId: 'skirts',
    name: 'Canon Peter Hall Pleated Pinafore Dress',
    color: 'Grey',
    gender: 'Girls',
    size: 'Age 7-8',
    description: 'Smart front-zip grey pinafore dress with heart zipper detail. Freshly laundered.',
    sizes: [
      { id: 'sz-6a', size: 'Age 5-6', stock: 1 },
      { id: 'sz-6b', size: 'Age 7-8', stock: 3 },
      { id: 'sz-6c', size: 'Age 9-10', stock: 2 }
    ],
    collectionNotes: 'Collect during reception opening hours.',
    featured: false,
    recentlyAdded: false,
    images: [
      'https://images.unsplash.com/photo-1582533561751-ef6f6ab93a2e?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-07-20'
  },
  {
    id: 'prod-7',
    schoolId: 'oasis-immingham',
    categoryId: 'ties',
    name: 'Oasis Academy Official Clip-On Tie',
    color: 'Navy with Silver Stripe',
    gender: 'Unisex',
    size: 'Standard Clip-On',
    description: 'Official clip-on academy tie. Quick release safety mechanism.',
    sizes: [
      { id: 'sz-7a', size: 'Standard Clip-On', stock: 8 },
      { id: 'sz-7b', size: 'Traditional Tie', stock: 4 }
    ],
    collectionNotes: 'Includes plastic storage sleeve.',
    featured: false,
    recentlyAdded: false,
    isPopular: true,
    images: [
      'https://images.unsplash.com/photo-1589756823695-278bc923f962?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-07-15'
  },
  {
    id: 'prod-8',
    schoolId: 'coomb-briggs',
    categoryId: 'bags',
    name: 'Coomb Briggs Book Bag with Shoulder Strap',
    color: 'Royal Blue',
    gender: 'Unisex',
    size: 'Standard Book Bag',
    description: 'High visibility reflective strip book bag with clear window tag for child name.',
    sizes: [
      { id: 'sz-8a', size: 'Standard Size', stock: 6 }
    ],
    collectionNotes: 'Sanitised and inspected.',
    featured: false,
    recentlyAdded: true,
    images: [
      'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-02'
  },
  {
    id: 'prod-9',
    schoolId: 'pilgrim',
    categoryId: 'coats',
    name: 'Pilgrim Academy Heavy Duty Waterproof Winter Coat',
    color: 'Navy Blue',
    gender: 'Unisex',
    size: 'Age 8-9',
    description: 'Fleece-lined waterproof stormproof winter parka with concealed hood. Keeps pupils warm in all weather.',
    sizes: [
      { id: 'sz-9a', size: 'Age 6-7', stock: 1 },
      { id: 'sz-9b', size: 'Age 8-9', stock: 2 },
      { id: 'sz-9c', size: 'Age 10-11', stock: 1 }
    ],
    collectionNotes: 'Warm fleece lining.',
    featured: true,
    recentlyAdded: true,
    isNewDonation: true,
    images: [
      'https://images.unsplash.com/photo-1544923246-77307dd654cb?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-08-03'
  },
  {
    id: 'prod-10',
    schoolId: 'eastfield',
    categoryId: 'polo-shirts',
    name: 'Eastfield White Embroidered Polo Shirt',
    color: 'White',
    gender: 'Unisex',
    size: 'Age 6-7 (26")',
    description: 'Soft cotton polo shirts with crisp embroidered Eastfield crest.',
    sizes: [
      { id: 'sz-10a', size: 'Age 5-6 (24")', stock: 3 },
      { id: 'sz-10b', size: 'Age 6-7 (26")', stock: 4 },
      { id: 'sz-10c', size: 'Age 8-9 (28")', stock: 2 }
    ],
    collectionNotes: 'Freshly laundered set.',
    featured: false,
    recentlyAdded: false,
    images: [
      'https://images.unsplash.com/photo-1583743814966-8936f5b7be1a?auto=format&fit=crop&w=800&q=80'
    ],
    createdAt: '2026-07-29'
  }
];

export const INITIAL_NEWS: NewsItem[] = [
  {
    id: 'news-1',
    title: 'Back to School Uniform Drive Ready!',
    image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80',
    description: 'We have stocked up on hundreds of blazers, jumpers, and PE kits ahead of the new school term. Reserve online now for free local pickup in Immingham!',
    published: true,
    category: 'Back to School',
    createdAt: '2026-08-01'
  },
  {
    id: 'news-2',
    title: 'PE Kits & Winter Coats Now Available',
    image: 'https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?auto=format&fit=crop&w=800&q=80',
    description: 'Thanks to generous local donations from Immingham families, we now have a full range of sports PE kit tops, tracksuits, and heavy-duty winter coats for all local schools.',
    published: true,
    category: 'New Stock',
    createdAt: '2026-08-03'
  },
  {
    id: 'news-3',
    title: 'Summer Holiday Collection Opening Times',
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
    description: 'Our community drop-off and pickup point at Bert Boyden Community Centre is open Tuesdays 10am - 2pm and Thursdays 3pm - 6pm throughout August.',
    published: true,
    category: 'Opening Hours',
    createdAt: '2026-07-28'
  }
];

export const INITIAL_STATS: ImpactStats = {
  familiesHelped: 0,
  itemsReused: 0,
  itemsDonated: 0,
  volunteers: 0,
  schoolsSupported: 0
};

export const INITIAL_SETTINGS: SiteSettings = {
  heroHeading: 'Free School Uniform for Immingham Families',
  heroSubheading: 'Helping local families save money by giving quality school uniforms a second life. Based at Bert Boyden Community Centre, Immingham.',
  collectionLocation: 'Bert Boyden Community Centre Drop Desk',
  collectionAddress: 'Bert Boyden Community Centre, Carver Road, Immingham, DN40 1LY',
  openingHours: formatOpeningHours(DEFAULT_OPENING_SCHEDULE),
  openingHoursSchedule: DEFAULT_OPENING_SCHEDULE,
  facebookUrl: 'https://facebook.com/UniformLoopImmingham',
  contactEmail: 'hello@uniformloop.org.uk',
  contactPhone: '01469 572400',
  reservationExpiryDays: 7,
  minimumProcessingDays: 2,
  bookingWindowDays: 30,
  popularThreshold: 10,
  enablePerformanceDiagnostics: false,
  maintenanceMode: false
};

export const FAQ_LIST = [
  {
    q: 'Is everything really completely free?',
    a: 'Yes! 100% free. Every uniform item listed on Uniform Loop has been donated by thoughtful local families in Immingham. There are no fees, no hidden costs, and no subscriptions.'
  },
  {
    q: 'Do I need to create a customer account or register?',
    a: 'No account is needed! Simply browse the shop, add the items your child needs to your reservation basket, and enter your contact details at checkout to reserve. Only volunteers have login accounts.'
  },
  {
    q: 'How long are my reserved items held for me?',
    a: 'Reservations are held for 7 days from the time of booking. You will receive an immediate reservation code (e.g. UL-84920) and collection instructions. If you cannot collect within 7 days, please contact us so we can hold them longer or return them to stock for another family.'
  },
  {
    q: 'Where do I collect my reserved items?',
    a: 'Collection takes place at the Bert Boyden Community Centre on Carver Road, Immingham (DN40 1LY) during configured Hub Opening Times.'
  },
  {
    q: 'Can somebody else collect on my behalf?',
    a: 'Yes, absolutely. As long as they bring your Reservation Reference Number (e.g. UL-84920) and parent name, our volunteers will hand over the items.'
  },
  {
    q: 'Can I reserve uniform for more than one child?',
    a: 'Yes! You can reserve multiple items in a single reservation session. We kindly ask families to reserve only what their children actually need so every family in Immingham can benefit.'
  },
  {
    q: 'How do I donate old school uniforms?',
    a: 'We welcome clean, good quality school uniforms for any Immingham school! You can drop off donations into our official donation bins located at Bert Boyden Community Centre reception or directly at participating school receptions.'
  },
  {
    q: 'What condition should donated items be in?',
    a: 'Please ensure all items are clean, freshly laundered, and in wearable condition with working zips and buttons. We inspect, wash, and grade all incoming donations before placing them online.'
  }
];

export const ACCEPTED_DONATIONS = [
  { title: 'Blazers', icon: 'Shirt', desc: 'School blazers with or without crests' },
  { title: 'Jumpers & Cardigans', icon: 'Vest', desc: 'Knitted V-necks, crewnecks, button cardigans' },
  { title: 'PE Kits & Tracksuits', icon: 'Activity', desc: 'Sports tees, shorts, skorts, PE hoodies & joggers' },
  { title: 'Trousers & Skirts', icon: 'Scissors', desc: 'School trousers, pleated skirts, pinafores & shorts' },
  { title: 'Shirts & Polo Tops', icon: 'Shirt', desc: 'White and coloured short/long sleeve tops' },
  { title: 'Coats & Jackets', icon: 'Shield', desc: 'Waterproof coats, parkas & winter jackets' },
  { title: 'School Shoes & Plimsolls', icon: 'Footprints', desc: 'Clean black leather shoes & PE footwear' },
  { title: 'Bags & Ties', icon: 'ShoppingBag', desc: 'Book bags, backpacks & school ties' }
];

export const INITIAL_CUSTOMER_MESSAGES: CustomerMessage[] = [];

